import { Button } from '@design-system';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { usePolicyReviewInProgress } from '../hooks/use-policy-review';
import type { ConsumerStepId } from '../lib/steps';
import { LinkList } from './link-list';

interface OtherWaysToPlanProps {
  clientId: string;
  returnTo: ConsumerStepId;
  /** Quiet links under quotes, or full cards when they're the only way forward. */
  appearance?: 'cards' | 'links';
}

/** The ways forward besides buying insurance: review a policy you own, or self-fund the care. */
export function OtherWaysToPlan({ clientId, returnTo, appearance = 'cards' }: OtherWaysToPlanProps) {
  const { goTo } = useConsumerFlow(clientId);
  const reviewInProgress = usePolicyReviewInProgress(clientId);
  const entries = [
    reviewInProgress
      ? {
          id: 'review-status',
          title: 'Check on my policy review',
          description: 'Your review is with our team. We’ll email your results.',
          onSelect: () => goTo('review-status'),
        }
      : {
          id: 'existing-policy',
          title: 'I already have a policy',
          description: 'Get the policy you own reviewed against this scenario.',
          onSelect: () => goTo('existing-policy'),
        },
    {
      id: 'self-fund',
      title: 'I want to self-fund the entire event',
      description: 'Plan these care costs with your own savings, income and assets.',
      onSelect: () => goTo('self-fund', { returnTo }),
    },
  ];

  if (appearance === 'links') {
    return (
      <div className="flex flex-wrap items-center gap-x-4">
        <span className="text-muted-foreground text-sm">Other ways to plan:</span>
        {entries.map(entry => (
          <Button key={entry.id} variant="link" className="px-0 has-[>svg]:px-0" onClick={entry.onSelect}>
            {entry.title}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <span className="text-muted-foreground text-sm">Other ways to plan</span>
      <LinkList className="sm:grid sm:grid-cols-2" entries={entries} />
    </div>
  );
}
